import omit from "lodash/omit";
import some from "lodash/some";
import uniqueId from "lodash/uniqueId";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_COMMUNICATION_TYPE_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_DEVELOPMENTAL_KEY,
  FIELD_ID_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_STATEMENT_ENABLED_KEY,
  FIELD_MARKED_FOR_REMOVAL_KEY,
  FIELD_METADATA_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY,
  FIELD_SKILLS_FRAMEWORK_IDS_KEY,
  FIELD_SUBTEMPLATE_KEY,
  FIELD_TEMPLATE_ID_KEY,
  FIELD_UNIQUE_ID_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { convertAPICustomFieldsToFormValues } from "PFApp/helpers/activity_form/data_conversions";
import { getPrivacyConfiguration } from "PFApp/helpers/activity_form/get_privacy_configuration";
import { useInterestQuestionariesTemplateConfiguration, useTemplate } from "PFApp/hooks";
import { useInitialQuestionsValues } from "PFApp/hooks/activity_form/use_initial_questions_values";
import { ManageRolesFormType } from "PFApp/types/activity_form/manage_roles_form_schema";
import { isProfileActive } from "PFCore/helpers/profile";
import { Activity, AvailabilityRule } from "PFCore/types";
import { ManageRolesViewRoleItem } from "PFCore/types/manage_roles_view";

export const useManageRolesInitialRoleData = () => {
  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const { getInitialQuestionsValues } = useInitialQuestionsValues({ template: roleTemplate });
  const templateConfig = useInterestQuestionariesTemplateConfiguration(roleTemplate);

  const getInitialRoleData = (item: ManageRolesViewRoleItem): ManageRolesFormType => {
    const { isDefaultPrivate } = getPrivacyConfiguration({
      template: roleTemplate,
      parentActivity: (item.engagement as Activity) ?? undefined
    });

    const { initialQuestionsValues } = getInitialQuestionsValues({
      inheritFromActivity: true,
      isClone: false,
      activity: item || ({} as Activity)
    });
    return {
      [FIELD_UNIQUE_ID_KEY]: uniqueId(),
      [FIELD_ID_KEY]: item.id,
      [FIELD_TEMPLATE_ID_KEY]: item.template_id,
      [FIELD_SUBTEMPLATE_KEY]: item.subtemplate_key ?? NO_SUBTEMPLATE_VALUE,
      [FIELD_NAME_KEY]: item.name,
      [FIELD_DESCRIPTION_KEY]: item.description,
      [FIELD_ASSIGNEE_ID_KEY]: isProfileActive(item.assignee?.profile)
        ? item?.assignee?.profile?.id || null
        : null,
      [FIELD_MIN_REQUIRED_COUNT_KEY]: item.min_required_count,
      [FIELD_PRIVACY_KEY]: !!item.private || isDefaultPrivate,
      [FIELD_AUDIENCES_KEY]: item.audiences || [],
      [FIELD_COMMUNICATION_TYPE_KEY]: item.communication_type || "direct",
      [FIELD_POST_UNTIL_KEY]: item.post_until ?? "",
      [FIELD_POST_UNTIL_CHANGE_REASON_KEY]: undefined,
      [FIELD_DEVELOPMENTAL_KEY]: item.developmental || false,
      [FIELD_SKILLS_FRAMEWORK_IDS_KEY]: item.skills_framework_ids || [],
      [FIELD_BOOKING_CATEGORY_ID_KEY]: item.metadata?.booking_category_id ?? undefined,
      [FIELD_CUSTOM_FIELDS_KEY]: convertAPICustomFieldsToFormValues(item.custom_fields || []),
      [FIELD_ATTACHMENT_IDS_KEY]: [], // TODO: [SP-7181] How to handle initial attachments data?
      [FIELD_INTEREST_QUESTIONS_KEY]: !some(initialQuestionsValues || [], Boolean)
        ? null
        : initialQuestionsValues,
      [FIELD_INTEREST_STATEMENT_ENABLED_KEY]: !!templateConfig?.showStatement,
      [FIELD_MARKED_FOR_REMOVAL_KEY]: false,
      [FIELD_METADATA_KEY]: {
        ...omit(item.metadata, ["interest_questions", "interest_statement_enabled", "booking_category_id"]),
        availability: item.metadata?.availability as AvailabilityRule | undefined
      },
      convertedAvailability: item.converted_availability,
      engagement_id: item.engagement?.id,
      engagement_name: item.engagement?.name,
      original_wm: item.rollforward_info?.source?.booked_profiles ?? [],
      booked_wm: item.booked_profiles ?? [],
      suggested_wm: item.suggested_profiles ?? []
    } as ManageRolesFormType;
  };

  return { getInitialRoleData };
};
